import { OIG_ADDON_BLOCKS } from "@services/payment";
import {
  resolveCheckoutFacilityType,
  resolveCheckoutFacilityTypeId,
  resolveCheckoutProfileSlug,
} from "@utils/pricingFacilityContext";

/**
 * Facility-purchase Select Plan catalog rules.
 *
 * Add-on SKUs (OIG Checks, Facility Network Overview, any plan_type/addon
 * marker) must not appear when buying a facility. Remaining rows must be
 * mapped to the selected facility type.
 *
 * When Rules/MCM omits facility-type mapping (today: adult_family_home),
 * type-specific names are treated as implicit maps:
 *   - "Professional for RHC | FQHC" → RHC/FQHC only
 *   - "Professional for other healthcare systems" → other healthcare, not
 *     residential add-by-type (AFH / ALF / memory care / independent living)
 * Generic combo / enterprise / contact-us rows stay visible.
 *
 * Follow-up: map adult_family_home (and other residential slugs) onto the
 * intended subscription SKUs in Rules/MCM so API facility_type /
 * primary_profile_slug filters are authoritative.
 */

const OIG_PLAN_IDS = new Set<number>(
  OIG_ADDON_BLOCKS.map((block) => block.planId),
);

const ADDON_TYPE_RE = /^(add[_-]?ons?)$/;
const ADDON_NAME_RE = /\badd[-\s_]?ons?\b/i;

const MAPPING_KEYS = [
  "facility_types",
  "facility_type_ids",
  "mapped_facility_types",
  "eligible_facility_types",
  "facility_type_slugs",
  "primary_profile_slugs",
  "profile_slugs",
  "facility_type",
  "facility_type_id",
  "primary_profile_slug",
  "facility_type_slug",
] as const;

/** Residential add-by-type slugs — not "other healthcare systems" unless mapped. */
const RESIDENTIAL_UNMAPPED_SLUGS = new Set([
  "adult_family_home",
  "assisted_living_facility",
  "memory_care",
  "independent_living",
]);

const TYPE_ALIASES: Record<string, readonly string[]> = {
  adult_family_home: ["afh", "adult_family_home", "adult_family_homes"],
  rural_health_clinic: ["rhc", "rural_health_clinic", "rural_health_clinics"],
  federally_qualified_health_center: [
    "fqhc",
    "federally_qualified_health_center",
    "federally_qualified_health_centers",
  ],
  skilled_nursing_facility: [
    "snf",
    "skilled_nursing_facility",
    "skilled_nursing",
  ],
  assisted_living_facility: [
    "alf",
    "assisted_living",
    "assisted_living_facility",
    "assisted_living_facilities",
  ],
};

export type SelectPlanFacilityHint = {
  id?: number | null;
  slug?: string | null;
  name?: string | null;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export function normalizeSelectPlanTypeToken(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function expandTypeTokens(tokens: Iterable<string>): Set<string> {
  const out = new Set<string>();
  for (const raw of tokens) {
    const token = normalizeSelectPlanTypeToken(raw);
    if (!token) continue;
    out.add(token);
    for (const [canonical, aliases] of Object.entries(TYPE_ALIASES)) {
      if (
        token === canonical ||
        aliases.includes(token) ||
        token.includes(canonical)
      ) {
        out.add(canonical);
        aliases.forEach((alias) => out.add(alias));
      }
    }
  }
  return out;
}

function pushMapping(
  target: { ids: number[]; tokens: string[] },
  value: unknown,
  depth = 0,
): void {
  if (value == null || depth > 4) return;
  if (typeof value === "number" && Number.isFinite(value)) {
    target.ids.push(value);
    return;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return;
    if (/^\d+$/.test(trimmed)) {
      target.ids.push(Number(trimmed));
    }
    target.tokens.push(normalizeSelectPlanTypeToken(trimmed));
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((entry) => pushMapping(target, entry, depth + 1));
    return;
  }
  const rec = asRecord(value);
  if (!rec) return;
  pushMapping(
    target,
    rec.id ?? rec.facility_type_id,
    depth + 1,
  );
  pushMapping(
    target,
    rec.type_slug ?? rec.slug ?? rec.primary_profile_slug ?? rec.facility_type_slug,
    depth + 1,
  );
  pushMapping(
    target,
    rec.display_name ?? rec.name ?? rec.facility_type,
    depth + 1,
  );
}

function collectPlanMappings(tier: Record<string, unknown>): {
  ids: number[];
  tokens: string[];
} {
  const target = { ids: [] as number[], tokens: [] as string[] };
  const take = (source: Record<string, unknown> | null) => {
    if (!source) return;
    for (const key of MAPPING_KEYS) {
      if (key in source) pushMapping(target, source[key]);
    }
  };
  take(tier);
  const details = tier.plan_details;
  const entries = Array.isArray(details) ? details : details ? [details] : [];
  entries.forEach((entry) => take(asRecord(entry)));
  return target;
}

function planDisplayName(tier: Record<string, unknown>): string {
  return [tier.display_name, tier.name, tier.plan_name, tier.tier_name]
    .map(text)
    .filter(Boolean)
    .join(" ");
}

function detailsEntries(tier: Record<string, unknown>): Record<string, unknown>[] {
  const details = tier.plan_details;
  if (Array.isArray(details)) {
    return details.map(asRecord).filter((row): row is Record<string, unknown> => !!row);
  }
  const single = asRecord(details);
  return single ? [single] : [];
}

export function isAddonPricingTier(tier: unknown): boolean {
  const rec = asRecord(tier);
  if (!rec) return false;
  if (rec.is_addon === true || rec.isAddon === true) return true;
  if (OIG_PLAN_IDS.has(Number(rec.id))) return true;

  const typeFields = [
    rec.plan_type,
    rec.planType,
    rec.category,
    rec.plan_category,
  ];
  if (
    typeFields.some((field) => {
      const raw = text(field);
      const token = normalizeSelectPlanTypeToken(raw);
      return ADDON_TYPE_RE.test(token) || ADDON_NAME_RE.test(raw);
    })
  ) {
    return true;
  }

  if (ADDON_NAME_RE.test(planDisplayName(rec))) return true;

  return detailsEntries(rec).some((entry) => {
    if (typeof entry.addon === "string" && entry.addon.trim()) return true;
    if (entry.is_addon === true) return true;
    const raw = text(entry.plan_type || entry.category || entry.plan_category);
    const token = normalizeSelectPlanTypeToken(raw);
    return ADDON_TYPE_RE.test(token) || ADDON_NAME_RE.test(raw);
  });
}

function hasFacilityHint(hint?: SelectPlanFacilityHint | null): boolean {
  if (!hint) return false;
  return (
    (typeof hint.id === "number" && Number.isFinite(hint.id)) ||
    !!normalizeSelectPlanTypeToken(hint.slug) ||
    !!normalizeSelectPlanTypeToken(hint.name)
  );
}

function facilityTokens(hint: SelectPlanFacilityHint): {
  ids: Set<number>;
  tokens: Set<string>;
} {
  const ids = new Set<number>();
  if (typeof hint.id === "number" && Number.isFinite(hint.id)) {
    ids.add(hint.id);
  }
  return {
    ids,
    tokens: expandTypeTokens([hint.slug || "", hint.name || ""]),
  };
}

function isRhcfqhcTokens(tokens: Set<string>): boolean {
  return [...tokens].some(
    (token) =>
      token === "rhc" ||
      token === "fqhc" ||
      token.includes("rural_health") ||
      token.includes("federally_qualified"),
  );
}

function isResidentialUnmapped(tokens: Set<string>): boolean {
  return [...tokens].some((token) => RESIDENTIAL_UNMAPPED_SLUGS.has(token));
}

type NameFamily = "rhc_fqhc" | "other_healthcare" | "named" | "generic";

function planNameFamily(name: string): { family: NameFamily; tokens: Set<string> } {
  const lower = name.toLowerCase();
  const token = normalizeSelectPlanTypeToken(name);
  if (
    /\brhc\b/.test(lower) ||
    /\bfqhc\b/.test(lower) ||
    lower.includes("rural health") ||
    lower.includes("federally qualified") ||
    token.includes("rural_health") ||
    token.includes("federally_qualified")
  ) {
    return { family: "rhc_fqhc", tokens: expandTypeTokens(["rhc", "fqhc"]) };
  }
  if (lower.includes("other healthcare") || token.includes("other_healthcare")) {
    return { family: "other_healthcare", tokens: new Set() };
  }

  const named = new Set<string>();
  for (const [canonical, aliases] of Object.entries(TYPE_ALIASES)) {
    if (token.includes(canonical) || aliases.some((alias) => token.includes(alias))) {
      named.add(canonical);
    }
  }
  if (named.size > 0) {
    return { family: "named", tokens: expandTypeTokens(named) };
  }
  return { family: "generic", tokens: new Set() };
}

export function planEligibleForFacilityPurchase(
  tier: unknown,
  hint?: SelectPlanFacilityHint | null,
): boolean {
  if (isAddonPricingTier(tier)) return false;
  if (!hasFacilityHint(hint)) return true;

  const rec = asRecord(tier);
  if (!rec) return false;

  const facility = facilityTokens(hint as SelectPlanFacilityHint);
  const mapping = collectPlanMappings(rec);
  const hasExplicit =
    mapping.ids.length > 0 ||
    mapping.tokens.some((token) => !!token);

  if (hasExplicit) {
    if (mapping.ids.some((id) => facility.ids.has(id))) return true;
    const mappedTokens = expandTypeTokens(mapping.tokens);
    return [...mappedTokens].some((token) => facility.tokens.has(token));
  }

  const { family, tokens } = planNameFamily(planDisplayName(rec));
  if (family === "rhc_fqhc") return isRhcfqhcTokens(facility.tokens);
  if (family === "other_healthcare") {
    return !isRhcfqhcTokens(facility.tokens) && !isResidentialUnmapped(facility.tokens);
  }
  if (family === "named") {
    return [...tokens].some((token) => facility.tokens.has(token));
  }
  return true;
}

export function filterFacilityPurchasePricingTiers<T>(
  tiers: T[] | null | undefined,
  hint?: SelectPlanFacilityHint | null,
): T[] {
  if (!Array.isArray(tiers)) return [];
  return tiers.filter((tier) => planEligibleForFacilityPurchase(tier, hint));
}

export function selectPlanFacilityHintFromContext(
  preferredFacility?: Record<string, unknown> | null,
  fallbackFacility?: Record<string, unknown> | null,
): SelectPlanFacilityHint {
  return {
    id: resolveCheckoutFacilityTypeId(preferredFacility, fallbackFacility),
    slug: resolveCheckoutProfileSlug(preferredFacility, fallbackFacility),
    name: resolveCheckoutFacilityType(preferredFacility, fallbackFacility),
  };
}

export function matchCatalogFacilityTypeId(
  catalog: {
    facility_types?: Array<{
      id?: number;
      type_slug?: string;
      display_name?: string;
    }>;
  } | null | undefined,
  slugOrName: string | null | undefined,
): number | undefined {
  const want = normalizeSelectPlanTypeToken(slugOrName);
  if (!want) return undefined;
  const rows = catalog?.facility_types || [];
  const match = rows.find((row) => {
    const rowSlug = normalizeSelectPlanTypeToken(row.type_slug);
    const rowName = normalizeSelectPlanTypeToken(row.display_name);
    if (rowSlug === want || rowName === want) return true;
    if (
      want === "skilled_nursing_facility" &&
      /skilled\s*nursing/i.test(String(row.display_name || ""))
    ) {
      return true;
    }
    if (
      want === "adult_family_home" &&
      (rowSlug === "afh" || /adult\s*family\s*home/i.test(String(row.display_name || "")))
    ) {
      return true;
    }
    return false;
  });
  const id = match?.id;
  return typeof id === "number" && Number.isFinite(id) ? id : undefined;
}

function snapshotToken(value: unknown): string {
  if (value == null) return "";
  const textValue = String(value).trim();
  if (!textValue || textValue === "null" || textValue === "undefined") return "";
  return textValue;
}

export type SelectPlanPricingRequestParts = {
  frequency: "monthly" | "yearly";
  facilityTypeId?: number | null;
  profileSlug?: string | null;
  vertical?: string | null;
  currency?: string | null;
  country?: string | null;
  ardcode?: string | null;
  preferredFacilityId?: number | null;
  directPlanId?: number | null;
  skuName?: string | null;
};

/** Stable catalog key. null/undefined/0 type ids collapse so AFH without catalog id does not refetch. */
export function selectPlanPricingRequestKey(
  parts: SelectPlanPricingRequestParts,
): string {
  const typeId =
    typeof parts.facilityTypeId === "number" &&
    Number.isFinite(parts.facilityTypeId) &&
    parts.facilityTypeId > 0
      ? String(parts.facilityTypeId)
      : "";
  const preferredId =
    typeof parts.preferredFacilityId === "number" &&
    Number.isFinite(parts.preferredFacilityId) &&
    parts.preferredFacilityId > 0
      ? String(parts.preferredFacilityId)
      : "";
  const planId =
    typeof parts.directPlanId === "number" &&
    Number.isFinite(parts.directPlanId) &&
    parts.directPlanId > 0
      ? String(parts.directPlanId)
      : "";
  return [
    parts.frequency === "monthly" ? "monthly" : "yearly",
    typeId,
    normalizeSelectPlanTypeToken(parts.profileSlug),
    snapshotToken(parts.vertical).toLowerCase() || "healthcare",
    snapshotToken(parts.currency).toUpperCase(),
    snapshotToken(parts.country).toLowerCase(),
    snapshotToken(parts.ardcode),
    preferredId,
    planId,
    snapshotToken(parts.skuName) || "SKU.Unicomply.Rules",
  ].join("|");
}

const FACILITY_SNAPSHOT_KEYS = [
  "id",
  "facility_id",
  "user_preferred_facility_id",
  "facility_type_id",
  "facility_type_slug",
  "primary_profile_slug",
  "facility_type",
  "vertical",
  "ccn_number",
  "npi_number",
] as const;

/** Ignore JSON.parse identity. AFH type id null vs undefined is the same snapshot. */
export function sameSelectPlanFacilitySnapshot(
  left: unknown,
  right: unknown,
): boolean {
  if (left === right) return true;
  const a = asRecord(left);
  const b = asRecord(right);
  if (!a && !b) return true;
  if (!a || !b) return false;
  return FACILITY_SNAPSHOT_KEYS.every(
    (key) => snapshotToken(a[key]) === snapshotToken(b[key]),
  );
}

